import { formatISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { handleHNErrors } from 'util/errorHandling';
import { captureException } from '@sentry/react';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useUpdateFirstRunState } from './useUpdateFirstRunState';
import { selectReviewOnboardSlice } from 'app/pages/store/reviewOnboardSlice';
import { FormType, NewCardFormValues } from 'app/pages/cards/cards/NewCardSheet/newCardUtils';
import { attachSpendRules, getAllGroups } from 'app/pages/store/groupsSlice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HNCardFormFactor } from 'API';
import type { GetCardsPayload } from 'CDK_API';
import { ListType } from 'app/pages/cards/spend-rules/utils';
import { triggerGroupUpdate, triggerGroupCardsReload } from 'app/pages/store/CardDataSlice/cardDataSlice';
import { getCard, issuePaymentCardForFinancialAccount } from 'app/pages/store/CardDataSlice/cardsThunks';

export interface NewCardSheetProps {
  [k: string]: any; // Allow any additional properties
  selectedPaymentCard?: GetCardsPayload;
  formType: FormType;
  loadPaymentCards?: () => Promise<void>;
  onUpdateCard?: () => void;
  handleClose?: () => void;
}

export const useIssueOrUpdateCard = (props: NewCardSheetProps) => {
  const { formType, selectedPaymentCard, onUpdateCard, loadPaymentCards, handleClose } = props;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { integration } = useAppSelector(selectReviewOnboardSlice);
  const { selectedPaidolId: selectedCompany } = useAppSelector(selectUserCompanies);
  const [loading, setLoading] = useState(false);

  // When ISSUE_CARD fails after the card is created (e.g. bad physical address), store the
  // orphaned card ID so the next submit retries with ORDER_PHYSICAL instead of creating a new card.
  const orphanedCardIdRef = useRef<string | null>(null);

  const { triggerUpdate } = useUpdateFirstRunState({
    operationName: 'completeFirstRunChecklist',
    firstRunValue: 'issueCard',
  });

  useEffect(() => {
    if (selectedCompany) {
      dispatch(getAllGroups(selectedCompany));
    }
  }, [dispatch, selectedCompany]);

  const onSaveCard = useCallback(
    async (form: NewCardFormValues) => {
      if (!integration || !selectedCompany || !integration.financialAccountId) {
        enqueueSnackbar(t('unknownError'), { variant: 'error' });
        captureException(
          new Error('Integration or selected company is not available when trying to save card'),
          { extra: { integration, selectedCompany } }
        );
        return;
      }

      setLoading(true);
      triggerUpdate();
      let hostname = window.location.origin;
      if (!window.location.origin) {
        const port = window.location.port ? `:${window.location.port}` : '';
        hostname = `${window.location.protocol}//${window.location.hostname}${port}`;
      }

      // If a previous attempt created the card but the physical order failed, retry as ORDER_PHYSICAL
      // on the already-created card rather than issuing a duplicate.
      const effectiveFormType = orphanedCardIdRef.current ? FormType.OrderPhysical : formType;
      const effectiveSelectedPaymentCardId = orphanedCardIdRef.current ?? selectedPaymentCard?.paymentCardId;

      const isPhysical = form.cardType === HNCardFormFactor.PHYSICAL;
      const isNotUpdate = effectiveFormType !== FormType.UpdateCard;

      // Helper function to transform ListType + array to { allowed, blocked } format
      // Defined inside callback to ensure proper closure access to formType
      const transformRestrictions = (
        restrictionType: ListType | undefined,
        restrictionValues: string[] | undefined
      ): { allowed?: string[]; blocked?: string[] } | null | undefined => {
        // Has restriction type with actual values -> send them
        if (
          restrictionType &&
          restrictionType !== ListType.NO_RESTRICTIONS &&
          restrictionValues &&
          restrictionValues.length > 0
        ) {
          return {
            allowed: restrictionType === ListType.ALLOW_LIST ? restrictionValues : undefined,
            blocked: restrictionType === ListType.BLOCK_LIST ? restrictionValues : undefined,
          };
        }

        // In update form: clear restrictions if either:
        // - Explicitly set to NO_RESTRICTIONS, OR
        // - Has ALLOW/BLOCK type but empty values (user deleted all chips)
        if (formType === FormType.UpdateCard) {
          if (restrictionType === ListType.NO_RESTRICTIONS) {
            return null;
          }
          if (restrictionType && (!restrictionValues || restrictionValues.length === 0)) {
            return null; // User deleted all items -> remove restrictions
          }
        }

        // Not set or not in update form - don't send field
        return undefined;
      };

      // Transform categories from ListType + array to { allowed, blocked } format
      const categories = transformRestrictions(form.spendingByCategoryType, form.spendingByCategory);

      // Transform countries from ListType + array to { allowed, blocked } format
      const countries = transformRestrictions(form.spendingByCountryType, form.spendingByCountry);

      dispatch(
        issuePaymentCardForFinancialAccount({
          input: {
            formType: effectiveFormType,
            selectedPaymentCardId: effectiveSelectedPaymentCardId,
            financialAccountId: integration.financialAccountId,
            ...(integration.cardProfileId && { cardProfileId: integration.cardProfileId }),
            cardExpirationDate: formatISO(form.cardExpirationDate),
            cardType: form.cardType as HNCardFormFactor,
            email: form.cardHolder,
            paidolId: selectedCompany,
            hostname,
            cardName: form.cardName,
            cardGroupId: form.cardGroupId,
            // Send null explicitly when unlimited is on, otherwise send the value
            maximumTransactionAmount: form.isMaximumTransactionAmountUnlimited
              ? null
              : form.maximumTransactionAmount,
            monthlySpendLimit: form.monthlySpendLimit,
            merchantSpendRuleId: form.merchantSpendRuleId,
            // Match spend rules form pattern:
            // - undefined = don't send field (don't change)
            // - null = send null (remove existing rules)
            // - object with values = send object (create/update rules)
            ...(categories !== undefined && { categories }),
            ...(countries !== undefined && { countries }),
            requireCardCode: form.requireCardCode,
            requireAddress: form.requireAddress,
            ...(isPhysical &&
              isNotUpdate && {
                addressFirstName: form.addressFirstName
                  ? form.addressFirstName.trim()
                  : form.addressFirstName,
                addressLastName: form.addressLastName ? form.addressLastName.trim() : form.addressLastName,
                addressStreet1: form.addressStreet1 ? form.addressStreet1.trim() : form.addressStreet1,
                addressStreet2: form.addressStreet2 ? form.addressStreet2.trim() : form.addressStreet2,
                addressZip: form.addressZip ? form.addressZip.trim() : form.addressZip,
                addressCity: form.addressCity ? form.addressCity.trim() : form.addressCity,
                addressState: form.addressState,
                shippingMethod: form.shippingMethod !== 'NO_SHIPPING' ? form.shippingMethod : undefined,
                requireSignature: form.requireSignature,
              }),
          },
        })
      )
        .unwrap()
        .then(async (response) => {
          orphanedCardIdRef.current = null;
          handleClose?.();

          if (form?.cardGroupId && form?.cardGroupId !== 'NO_GROUP') {
            selectedPaymentCard &&
              selectedCompany &&
              dispatch(
                getCard({
                  filter: {
                    and: [{ or: [{ paymentCardId: { eq: selectedPaymentCard?.paymentCardId } }] }],
                    paidolId: { eq: selectedCompany },
                  },
                })
              );
            await dispatch(attachSpendRules({ cardGroupId: form.cardGroupId }));
          } else {
            console.warn('No card group selected, skipping spend rules attachment');
          }

          enqueueSnackbar(
            effectiveFormType === FormType.IssueCard
              ? 'Card has been successfully issued.'
              : effectiveFormType === FormType.OrderPhysical
              ? 'Physical card has been successfully ordered.'
              : 'Changes to card have been saved.',
            {
              variant: 'success',
            }
          );
        })
        .catch((error) => {
          captureException(error);

          // If the card was created but the physical order failed, the backend returns the
          // paymentCardId so we can retry ORDER_PHYSICAL on the existing card instead of
          // creating a duplicate on the next submit.
          if (error?.paymentCardId) {
            orphanedCardIdRef.current = error.paymentCardId;
          }

          // Any error means we cannot confirm the full operation succeeded — leave the form open.
          if (handleHNErrors(error, enqueueSnackbar, 'An error has occurred:')) return;

          enqueueSnackbar(t('unknownError'), { variant: 'error' });
          throw error;
        })
        .finally(() => {
          setLoading(false);
          onUpdateCard?.();
          // Don't reset groups slice - it clears group cards that are being displayed
          // For new cards (ISSUE_CARD, ORDER_PHYSICAL), trigger card reload
          // For updates (UPDATE_CARD), only trigger metadata update
          if (effectiveFormType === FormType.IssueCard || effectiveFormType === FormType.OrderPhysical) {
            dispatch(triggerGroupCardsReload());
          } else {
            dispatch(triggerGroupUpdate());
          }
        });
    },
    [
      dispatch,
      enqueueSnackbar,
      formType,
      handleClose,
      integration,
      loadPaymentCards,
      onUpdateCard,
      selectedCompany,
      selectedPaymentCard,
      t,
      triggerUpdate,
    ]
  );

  return { onSaveCard, loading, formType };
};
